/* @flow */
import * as React from 'react';
import type { TFunction } from 'react-i18next';
import type { ImportPreviewRow } from 'api/brandsBulkImport';
import styles from './ImportShared.module.scss';
import ImportErrorList from './ImportErrorList';
import { formatNullable, formatFieldLabel } from './formatters';

type TypeProps = {
  rows: ImportPreviewRow[],
  type: 'brands' | 'accounts',
  t: TFunction,
};

const ImportRowList = ({ rows, type, t }: TypeProps): React.Node => {
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <ul className={styles['import-row-list']}>
      {rows.map((row: ImportPreviewRow) => {
        const changes = row.changes || {};
        const changeKeys = Object.keys(changes);
        const hasAlerts =
          (row.warnings && row.warnings.length > 0) || (row.errors && row.errors.length > 0);

        return (
          <li key={`${type}-${row.row}`} className={styles['import-row-list__item']}>
            <div className={styles['import-row-list__meta']}>
              <span className={styles['import-row-list__row-number']}>
                {t('general:reporting.brand_form.multiple.labels.row_number', { row: row.row })}
              </span>
              <span
                className={`${styles['import-row-list__action']} ${
                  styles[`import-row-list__action--${row.action}`]
                }`}
              >
                {t(`general:reporting.brand_form.multiple.labels.action_${row.action}`)}
              </span>
              {type === 'brands' && row.brand_id && (
                <span className={styles['import-row-list__id']}>
                  <span className={styles['import-row-list__id-label']}>
                    {t('general:reporting.brand_form.multiple.labels.brand_id')}:
                  </span>{' '}
                  <span className={styles['import-row-list__id-value']}>{row.brand_id}</span>
                </span>
              )}
              {type === 'accounts' && (
                <div className={styles['import-row-list__id']}>
                  <div className={styles['import-row-list__id-line']}>
                    <span className={styles['import-row-list__id-label']}>
                      {t('general:reporting.brand_form.multiple.labels.account_no')}:
                    </span>{' '}
                    <span className={styles['import-row-list__id-value']}>
                      {formatNullable(row.account_no)}
                    </span>
                  </div>
                  <div className={styles['import-row-list__id-line']}>
                    <span className={styles['import-row-list__id-label']}>
                      {t('general:reporting.brand_form.multiple.labels.er_company_code')}:
                    </span>{' '}
                    <span className={styles['import-row-list__id-value']}>
                      {formatNullable(row.er_company_code)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className={styles['import-row-list__content']}>
              {hasAlerts && (
                <React.Fragment>
                  {row.warnings && row.warnings.length > 0 && (
                    <ImportErrorList errors={row.warnings} variant="warning" t={t} />
                  )}
                  {row.errors && row.errors.length > 0 && (
                    <ImportErrorList errors={row.errors} variant="error" t={t} />
                  )}
                </React.Fragment>
              )}
              {changeKeys.length > 0 ? (
                <div className={styles['import-row-list__changes']}>
                  {changeKeys.map((field: string) => {
                    const change = changes[field];
                    const fromValue = change ? change.from : undefined;
                    const hasFrom =
                      fromValue !== undefined && fromValue !== null && String(fromValue) !== '';
                    return (
                      <div key={field} className={styles['import-row-list__change']}>
                        <span className={styles['import-row-list__field']}>
                          {formatFieldLabel(field)}
                        </span>
                        <span className={styles['import-row-list__from-to']}>
                          {hasFrom ? `${formatNullable(fromValue)} → ` : '→ '}
                          {formatNullable(change ? change.to : undefined)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                !hasAlerts && (
                  <span className={styles['import-row-list__no-changes']}>
                    {t('general:reporting.brand_form.multiple.labels.no_changes')}
                  </span>
                )
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default ImportRowList;
